import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchErrors, fetchErrorOccurrences } from '@/lib/admin/errors';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

export default function ErrorsPage() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  const { data: groups } = useQuery({
    queryKey: ['admin', 'errors'],
    queryFn: fetchErrors,
  });

  const { data: occurrences } = useQuery({
    queryKey: ['admin', 'errors', selected],
    queryFn: () => fetchErrorOccurrences(selected!),
    enabled: !!selected,
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.errors')}</h1>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2">{t('admin.columns.signature')}</th>
              <th className="px-4 py-2">{t('admin.columns.message')}</th>
              <th className="px-4 py-2">{t('admin.columns.count')}</th>
              <th className="px-4 py-2">{t('admin.columns.users')}</th>
              <th className="px-4 py-2">{t('admin.columns.lastSeen')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(groups ?? []).length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">{t('common.noResults')}</td></tr>
            )}
            {(groups ?? []).map((g) => (
              <tr key={g.signature} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelected(g.signature)}>
                <td className="px-4 py-2 font-mono text-xs">{g.signature}</td>
                <td className="px-4 py-2 truncate max-w-md">{g.sampleMessage}</td>
                <td className="px-4 py-2"><span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">{g.count}</span></td>
                <td className="px-4 py-2 text-xs text-gray-600">{g.affectedUsersCount}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{new Date(g.lastSeen).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Transition show={!!selected} as={Fragment}>
        <Dialog onClose={() => setSelected(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/40" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
              <Dialog.Title className="font-mono text-sm font-semibold text-gray-900">{selected}</Dialog.Title>
              <div className="mt-4 space-y-3">
                {(occurrences ?? []).map((o) => (
                  <div key={o.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{new Date(o.occurredAt).toLocaleString()}</span>
                      <span>{o.url ?? ''}</span>
                    </div>
                    <div className="mt-1 text-sm">{o.message}</div>
                    {o.stack && (
                      <pre className="mt-2 max-h-64 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
{o.stack}
                      </pre>
                    )}
                  </div>
                ))}
                {(occurrences ?? []).length === 0 && <p className="text-sm text-gray-500">{t('common.noResults')}</p>}
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={() => setSelected(null)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">{t('common.close')}</button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
